import { selectSelectedApplicantsIds } from '../../../../store/workspace/selectors';
import { setSelectedApplicantsIds } from '../../../../store/workspace/slice';
import { useDispatch, useSelector } from '../../../../store/store';
import { useCallback } from 'react';

interface SelectableApplicant {
    userId: string;
};

/**
 * Bulk selection of applicants, shared by every selectable statistics table.
 *
 * `selectableApplicants` is the list currently rendered by the table: the
 * "select all" toggle only ever operates on what the user can actually see.
 */
export function useApplicantSelection(selectableApplicants: SelectableApplicant[]) {
    const dispatch = useDispatch();
    const selectedApplicantsIds = useSelector(selectSelectedApplicantsIds);

    const isSelected = useCallback(
        (applicantId: string) => selectedApplicantsIds.includes(applicantId),
        [selectedApplicantsIds],
    );

    const toggleApplicant = useCallback((applicantId: string) => {
        const next = selectedApplicantsIds.includes(applicantId)
            ? selectedApplicantsIds.filter(id => id !== applicantId)
            : [...selectedApplicantsIds, applicantId];

        dispatch(setSelectedApplicantsIds(next));
    }, [dispatch, selectedApplicantsIds]);

    const allSelected = selectableApplicants.length !== 0 &&
        selectableApplicants.length === selectedApplicantsIds.length;

    const toggleAll = useCallback(() => {
        dispatch(setSelectedApplicantsIds(
            allSelected ? [] : selectableApplicants.map(applicant => applicant.userId),
        ));
    }, [dispatch, allSelected, selectableApplicants]);

    return { isSelected, toggleApplicant, toggleAll, allSelected };
};
